/**
 * ROL de un archivo/plano dentro de la HP POR-PROYECTO — helpers PUROS (sin DB, sin IA).
 *
 * Un proyecto de planos = VARIOS DWG/IFC que se COMPLEMENTAN. Cada archivo entra a la MISMA Hoja de
 * Presupuesto con un rol que decide si suma cantidad (planta de edificio / de sitio) o solo enriquece
 * (lámina de detalles/especificaciones). Ese rol NO se adivina por el nombre del archivo (nada de
 * tabla de sufijos ARQ/DET/URB): se PROPONE por el CONTENIDO del inventario y el usuario lo confirma.
 *
 * Regla del repo (no hardcode de dominio): la derivación NO usa un catálogo de disciplinas ni un mapa
 * capa→rol. Solo mira una señal ESTRUCTURAL, auto-descrita por el propio dibujo: ¿hay contenido
 * MEDIBLE (bloques contables, recintos cerrados, geometría con cantidad) o es puro trazo de detalle?
 * La distinción edificio↔sitio es semántica y se DELEGA al usuario (default `cuantificable_edificio`).
 */
import type { PlanoInventario, PlanoRolArchivo } from './domain.js';
import { esNotaDeEspecificacionConMedida, partirListaNumerada } from './especificacionDeLamina.js';

/** Cuántas notas de la lámina especifican un elemento con medida (ver `esNotaDeEspecificacionConMedida`). */
function notasDeEspecificacion(inv: PlanoInventario): number {
  return (inv.anotaciones?.textos ?? [])
    .flatMap((t) => partirListaNumerada(t ?? ''))
    .filter((t) => esNotaDeEspecificacionConMedida(t)).length;
}

/** ¿Alguna vista del dibujo es una planta (puertas con abatimiento, según el lector)? */
function tieneVistaDePlanta(inv: PlanoInventario): boolean {
  return (inv.vistas ?? []).some((v) => v.tipo === 'planta' || v.clase === 'planta');
}

/** Opción de rol para pintar el selector en la UI (español-MX). */
export interface PlanoRolArchivoOpcion {
  id: PlanoRolArchivo;
  label: string;
  ayuda: string;
}

/**
 * Catálogo de roles para el selector. Son DOS opciones porque el comportamiento es uno de dos: el plano suma
 * cantidad a la hoja o solo sirve de consulta. «Edificio» y «sitio» se comportaban igual (ningún cálculo los
 * distingue), así que la pantalla ya no los separa; el dato guardado puede seguir siendo `cuantificable_sitio`
 * y se muestra como «Suma a la hoja».
 */
export const PLANO_ROLES_ARCHIVO: readonly PlanoRolArchivoOpcion[] = [
  {
    id: 'cuantificable_edificio',
    label: 'Suma a la hoja',
    ayuda: 'Sus cantidades entran a la hoja de presupuesto (plantas, cortes, urbanización y obra exterior).',
  },
  {
    id: 'referencia_spec',
    label: 'Solo referencia',
    ayuda: 'Láminas de detalle o especificaciones: ayudan a describir los conceptos, pero no suman cantidad.',
  },
] as const;

/** El valor del selector para un rol guardado: `cuantificable_sitio` se ve (y se elige) como «Suma a la hoja». */
export function rolParaSelector(rol: PlanoRolArchivo): PlanoRolArchivo {
  return rol === 'referencia_spec' ? 'referencia_spec' : 'cuantificable_edificio';
}

/** Etiqueta corta de un rol para mostrarlo junto a cada renglón. */
export function etiquetaRolArchivo(rol: PlanoRolArchivo): string {
  const op = PLANO_ROLES_ARCHIVO.find((r) => r.id === rolParaSelector(rol));
  // Un valor fuera del tipo (dato viejo o ajeno) se devuelve tal cual.
  return op && (rol === 'referencia_spec' || rol === 'cuantificable_edificio' || rol === 'cuantificable_sitio') ? op.label : rol;
}

/**
 * ¿Un archivo con este rol APORTA cantidad al total de la HP? Gancho anti-doble-conteo: un archivo
 * de `referencia_spec` (láminas de detalle) NO suma cantidad — solo enriquece los conceptos de los
 * archivos cuantificables. Los dos roles cuantificables sí aportan.
 */
export function rolArchivoAportaCantidad(rol: PlanoRolArchivo): boolean {
  return rol !== 'referencia_spec';
}

/**
 * La señal estructural de "hay algo que medir": bloques contables (instancias), o capas con recintos
 * cerrados / área / longitud / cuantificación / medidas del modelo.
 */
export function contenidoMedible(inv: PlanoInventario): { bloquesContables: boolean; capaMedible: boolean } {
  const bloquesContables = (inv.bloques ?? []).some((b) => (b.n ?? 0) > 0);
  const capaMedible = (inv.capas ?? []).some((c) => {
    const geometria =
      (c.area ?? 0) > 0 ||
      (c.area_recintos ?? 0) > 0 ||
      (c.longitud ?? 0) > 0 ||
      (c.corrido ?? 0) > 0 ||
      (c.recintos ?? 0) > 0;
    const cuant = (c.cuantificacion?.cantidad ?? 0) > 0;
    const m = c.medidas;
    const medidasModelo =
      !!m && ((m.conteo ?? 0) > 0 || (m.area ?? 0) > 0 || (m.longitud ?? 0) > 0 || (m.volumen ?? 0) > 0);
    return geometria || cuant || medidasModelo;
  });

  return { bloquesContables, capaMedible };
}

/**
 * PROPONE el rol de un archivo por su CONTENIDO (no por su nombre). Señal estructural pura:
 *
 *  - Si el inventario trae contenido MEDIBLE — bloques contables (instancias), o capas con recintos
 *    cerrados / área / longitud / cuantificación / medidas del modelo — es un plano que MIDE obra →
 *    `cuantificable_edificio` (default; el usuario lo cambia a `cuantificable_sitio` si es urbanización,
 *    distinción semántica que no se adivina sin tabla de dominio).
 *  - Si NO trae nada medible (puro trazo/detalle, textos y cotas de lámina) → `referencia_spec`.
 *  - Si trae trazo medible pero es una LÁMINA DE DETALLES — ninguna vista es planta (sin puertas con
 *    abatimiento), sin piezas contables y con notas que especifican elementos con medida (`esNotaDeEspecificacionConMedida`)
 *    → `referencia_spec`.
 *  - Sin inventario todavía (plano recién encolado) → `cuantificable_edificio` provisional.
 *
 * Devuelve el rol sugerido y una razón corta en español-MX para mostrarla en la UI.
 */
export function derivarRolArchivoSugerido(
  inventario: PlanoInventario | null | undefined,
): { rol: PlanoRolArchivo; razon: string } {
  if (!inventario) {
    return { rol: 'cuantificable_edificio', razon: 'Aún sin leer el plano; se asume que mide obra.' };
  }

  // PDF leído por visión: no hay geometría (bloques/capas), pero las cantidades ESTIMADAS que se
  // transcribieron son justamente lo que aporta a la HP → se propone que mida obra. Sin cantidades,
  // cae por el camino de abajo a `referencia_spec` (una lámina que no aportó ninguna medida).
  if (inventario.estimadoVision && (inventario.cantidadesEstimadas ?? []).length > 0) {
    return {
      rol: 'cuantificable_edificio',
      razon: 'Se leyeron cantidades del PDF (estimadas); se propone que aporte cantidad.',
    };
  }

  const { bloquesContables, capaMedible } = contenidoMedible(inventario);

  // IFC 4.3 de infraestructura: la estructura espacial del propio modelo dice qué es. Si todas sus
  // instalaciones (IfcFacility) son de infraestructura —puente, carretera, vía férrea, marítima— y
  // ninguna es un edificio, mide obra de sitio. Un modelo con edificio sigue midiendo el edificio.
  const instalaciones = inventario.ifc?.instalaciones ?? [];
  if (
    (bloquesContables || capaMedible) &&
    instalaciones.length > 0 &&
    instalaciones.every((i) => i.toUpperCase() !== 'IFCBUILDING')
  ) {
    return {
      rol: 'cuantificable_sitio',
      razon: 'El modelo es de infraestructura (puente, carretera o vía, no un edificio); se propone que mida el sitio.',
    };
  }

  // Lámina de DETALLES con notas de especificación: también tiene trazo con longitud y área (el corte
  // del muro, la sección de la dala), así que "tiene geometría" no la distingue de una planta. Lo que sí
  // la distingue, sin leer nombres de capa ni de archivo: ninguna de sus vistas es una planta (no hay
  // puertas con abatimiento, la señal geométrica del lector), no tiene piezas contables, y sus textos
  // ESPECIFICAN elementos (notas con magnitud técnica, no nombres de local ni rótulos).
  const specs = notasDeEspecificacion(inventario);
  if (specs > 0 && !bloquesContables && !tieneVistaDePlanta(inventario)) {
    return {
      rol: 'referencia_spec',
      razon: `La lámina no tiene plantas ni piezas que contar y trae ${specs} nota${specs === 1 ? '' : 's'} de especificación; se propone como sola referencia.`,
    };
  }

  if (bloquesContables || capaMedible) {
    return {
      rol: 'cuantificable_edificio',
      razon: 'El plano trae elementos y geometría medibles; se propone que aporte cantidad.',
    };
  }
  return {
    rol: 'referencia_spec',
    razon: 'El plano es trazo de detalle sin elementos medibles; se propone como sola referencia.',
  };
}
